import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ROLES } from '@bcc/shared';
import { SITE_FORM_CONTROL, SITE_GLASS_CARD } from '../constants/siteUi';
import { apiFetch } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

type ProviderKind = 'openai' | 'anthropic' | 'custom_openai';

type MeAiApiSummary = {
  analyzer_ready: boolean;
  personal_keys_locked: boolean;
  using_organization_fallback: boolean;
  effective_provider: ProviderKind | null;
  personal: {
    analyzer_ready: boolean;
    openai_configured: boolean;
    anthropic_configured: boolean;
    custom_openai_configured: boolean;
    openai_model: string | null;
    anthropic_model: string | null;
    custom_openai_model: string | null;
    active_provider: ProviderKind | null;
  };
  organization: {
    analyzer_ready: boolean;
    openai: { stored_key: boolean; environment_fallback: boolean };
    anthropic: { stored_key: boolean; environment_fallback: boolean };
    custom_openai: { configured: boolean };
    active_provider: ProviderKind | null;
  };
};

function providerLabel(p: ProviderKind | null): string {
  if (p === 'custom_openai') return 'Custom';
  if (p === 'anthropic') return 'Anthropic';
  if (p === 'openai') return 'OpenAI';
  return 'auto';
}

/** Your Deal Analyzer keys (portal); organization defaults apply when allowed and you have no usable personal keys. */
export function InvestorAiApiSettings() {
  const { session, role } = useAuth();
  const token = session?.access_token;
  const [summary, setSummary] = useState<MeAiApiSummary | null>(null);
  const [openaiKey, setOpenaiKey] = useState('');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [customBaseUrl, setCustomBaseUrl] = useState('');
  const [customApiKey, setCustomApiKey] = useState('');
  const [openaiModel, setOpenaiModel] = useState('');
  const [anthropicModel, setAnthropicModel] = useState('');
  const [customModel, setCustomModel] = useState('');
  const [provider, setProvider] = useState<ProviderKind>('openai');
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setErr(null);
    try {
      const res = await apiFetch<MeAiApiSummary>('/api/me/ai-api-settings', { token });
      setSummary(res);
      setOpenaiModel(res.personal.openai_model ?? '');
      setAnthropicModel(res.personal.anthropic_model ?? '');
      setCustomModel(res.personal.custom_openai_model ?? '');
      const ap = res.personal.active_provider;
      if (ap === 'openai' || ap === 'anthropic' || ap === 'custom_openai') {
        setProvider(ap);
      } else if (res.personal.custom_openai_configured && !res.personal.openai_configured && !res.personal.anthropic_configured) {
        setProvider('custom_openai');
      } else if (res.personal.anthropic_configured && !res.personal.openai_configured) {
        setProvider('anthropic');
      } else {
        setProvider('openai');
      }
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveKeys(e: FormEvent) {
    e.preventDefault();
    if (!token || summary?.personal_keys_locked) return;
    setErr(null);
    setOk(null);
    setLoading(true);
    try {
      const patch: Record<string, unknown> = {
        active_provider: provider,
        openai_model: openaiModel.trim() || null,
        anthropic_model: anthropicModel.trim() || null,
        custom_openai_model: customModel.trim() || null,
      };
      if (openaiKey.trim()) patch.openai_api_key = openaiKey.trim();
      if (anthropicKey.trim()) patch.anthropic_api_key = anthropicKey.trim();
      if (customBaseUrl.trim()) patch.custom_openai_base_url = customBaseUrl.trim();
      if (customApiKey.trim()) patch.custom_openai_api_key = customApiKey.trim();
      await apiFetch<{ summary: MeAiApiSummary }>('/api/me/ai-api-settings', {
        method: 'PATCH',
        token,
        body: JSON.stringify(patch),
      });
      setOpenaiKey('');
      setAnthropicKey('');
      setCustomApiKey('');
      setOk('Saved.');
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function clearOpenAiKey() {
    if (!token || summary?.personal_keys_locked) return;
    setErr(null);
    setOk(null);
    setLoading(true);
    try {
      await apiFetch<{ summary: MeAiApiSummary }>('/api/me/ai-api-settings', {
        method: 'PATCH',
        token,
        body: JSON.stringify({ openai_api_key: '' }),
      });
      setOk('Stored OpenAI key removed.');
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function clearAnthropicKey() {
    if (!token || summary?.personal_keys_locked) return;
    setErr(null);
    setOk(null);
    setLoading(true);
    try {
      await apiFetch<{ summary: MeAiApiSummary }>('/api/me/ai-api-settings', {
        method: 'PATCH',
        token,
        body: JSON.stringify({ anthropic_api_key: '' }),
      });
      setOk('Stored Anthropic key removed.');
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function clearCustomProvider() {
    if (!token || summary?.personal_keys_locked) return;
    setErr(null);
    setOk(null);
    setLoading(true);
    try {
      await apiFetch<{ summary: MeAiApiSummary }>('/api/me/ai-api-settings', {
        method: 'PATCH',
        token,
        body: JSON.stringify({
          custom_openai_api_key: '',
          custom_openai_base_url: '',
          custom_openai_model: null,
        }),
      });
      setCustomBaseUrl('');
      setOk('Stored custom provider removed.');
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  const personalNeedsExplicitChoice = Boolean(
    !summary?.personal_keys_locked &&
      [
        summary?.personal.openai_configured,
        summary?.personal.anthropic_configured,
        summary?.personal.custom_openai_configured,
      ].filter(Boolean).length >= 2 &&
      summary &&
      !summary.personal.active_provider &&
      !summary.personal.analyzer_ready,
  );

  const locked = Boolean(summary?.personal_keys_locked);

  return (
    <div className="space-y-6">
      <div>
        <p className="font-display text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-[var(--home-miami-blue)]">
          Investor portal
        </p>
        <h1 className="mt-2 font-body text-2xl font-semibold text-[var(--home-text-on-dark)]">
          Deal Analyzer — AI API settings
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-[var(--home-muted-on-dark)]">
          {locked ? (
            <>
              Your administrator enabled <strong className="text-[var(--home-text-on-dark)]">Global organization keys</strong>. Personal
              keys cannot be saved or edited here.
            </>
          ) : (
            <>
              Keys you save here are used for your account when you run the Deal Analyzer and override organization defaults when
              allowed. Custom uses an OpenAI-compatible HTTPS endpoint.
            </>
          )}
        </p>
        <Link
          to="/dashboard/analyzer"
          className="mt-3 inline-block text-sm font-medium text-[var(--home-miami-blue)] underline-offset-4 hover:underline"
        >
          ← Back to Deal Analyzer
        </Link>
      </div>

      <div className={`p-6 ${SITE_GLASS_CARD}`}>
        {summary ? (
          <>
            <div className="mb-6 flex flex-wrap items-center gap-3 border-b border-white/[0.08] pb-6">
              <span className="text-sm font-medium text-[var(--home-text-on-dark)]">Your analyzer</span>
              <span
                className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                  summary.analyzer_ready ? 'bg-emerald-500/20 text-emerald-200' : 'bg-amber-500/15 text-amber-200'
                }`}
              >
                {summary.analyzer_ready ? 'Ready' : 'Locked'}
              </span>
              {summary.analyzer_ready ? (
                <span className="text-xs text-[var(--home-muted-on-dark)]">
                  Using{' '}
                  <strong className="text-[var(--home-text-on-dark)]">{providerLabel(summary.effective_provider)}</strong>
                  {summary.personal_keys_locked ? (
                    <span className="text-[var(--home-muted-on-dark)]"> (organization credentials)</span>
                  ) : summary.using_organization_fallback ? (
                    <span className="text-[var(--home-muted-on-dark)]"> (organization defaults)</span>
                  ) : (
                    <span className="text-[var(--home-muted-on-dark)]"> (your keys)</span>
                  )}
                </span>
              ) : null}
            </div>

            {summary.personal_keys_locked ? (
              <div className="mb-6 rounded-xl border border-amber-400/35 bg-amber-950/45 px-4 py-3 text-sm text-amber-100 shadow-lg shadow-black/15 ring-1 ring-amber-400/15">
                <p className="font-semibold text-amber-200">Personal API keys are locked</p>
                <p className="mt-2 text-amber-100/95">
                  Only organization credentials apply. Contact an administrator if you need a change.
                </p>
              </div>
            ) : null}

            {!summary.personal_keys_locked && summary.using_organization_fallback ? (
              <div className="mb-6 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-[var(--home-muted-on-dark)]">
                No complete personal configuration yet — the Deal Analyzer is using{' '}
                <strong className="text-[var(--home-text-on-dark)]">organization defaults</strong>. Add your own keys below when allowed.
              </div>
            ) : null}

            {!summary.personal_keys_locked && personalNeedsExplicitChoice ? (
              <div className="mb-6 rounded-xl border border-amber-400/35 bg-amber-950/35 px-4 py-3 text-sm text-amber-100">
                Multiple providers are saved on your account — choose which runs the analyzer below, then save.
              </div>
            ) : null}

            <ul className="mb-6 space-y-3 border-b border-white/[0.08] pb-6 text-sm">
              <li className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-[var(--home-text-on-dark)]">Your OpenAI</span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                    summary.personal.openai_configured
                      ? 'bg-emerald-500/20 text-emerald-200'
                      : 'bg-white/10 text-[var(--home-muted-on-dark)]'
                  }`}
                >
                  {summary.personal.openai_configured ? 'Key saved' : 'No key'}
                </span>
              </li>
              <li className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-[var(--home-text-on-dark)]">Your Anthropic</span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                    summary.personal.anthropic_configured
                      ? 'bg-emerald-500/20 text-emerald-200'
                      : 'bg-white/10 text-[var(--home-muted-on-dark)]'
                  }`}
                >
                  {summary.personal.anthropic_configured ? 'Key saved' : 'No key'}
                </span>
              </li>
              <li className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-[var(--home-text-on-dark)]">Your custom</span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                    summary.personal.custom_openai_configured
                      ? 'bg-emerald-500/20 text-emerald-200'
                      : 'bg-white/10 text-[var(--home-muted-on-dark)]'
                  }`}
                >
                  {summary.personal.custom_openai_configured ? 'Configured' : 'Not configured'}
                </span>
              </li>
              <li className="flex flex-wrap gap-x-3 gap-y-1 border-t border-white/[0.06] pt-3 text-[11px] text-[var(--home-muted-on-dark)]">
                <span className="font-medium text-[var(--home-text-on-dark)]">
                  {summary.personal_keys_locked ? 'Organization' : 'Org fallback'}
                </span>
                <span>
                  OpenAI {summary.organization.openai.stored_key ? 'saved' : '—'}
                  {summary.organization.openai.environment_fallback ? ' · env' : ''}
                </span>
                <span>
                  Anthropic {summary.organization.anthropic.stored_key ? 'saved' : '—'}
                  {summary.organization.anthropic.environment_fallback ? ' · env' : ''}
                </span>
                <span>Custom {summary.organization.custom_openai.configured ? 'yes' : '—'}</span>
              </li>
            </ul>
          </>
        ) : null}

        {role === ROLES.ADMIN ? (
          <p className="mb-4 text-xs text-[var(--home-muted-on-dark)]">
            Organization-wide defaults (including Global lock) are managed under{' '}
            <Link to="/admin/ai-api-settings" className="text-[var(--home-miami-blue)] underline-offset-2 hover:underline">
              Admin → AI API Settings
            </Link>
            .
          </p>
        ) : null}

        {err ? <p className="mb-4 text-sm text-red-400">{err}</p> : null}
        {ok ? <p className="mb-4 text-sm text-emerald-400">{ok}</p> : null}

        <form className="space-y-5" onSubmit={saveKeys}>
          <fieldset
            disabled={locked}
            className="min-w-0 space-y-5 border-0 p-0 disabled:pointer-events-none disabled:opacity-45"
          >
            <fieldset className="space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <legend className="px-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
                Provider (your keys only)
              </legend>
              <label className="flex cursor-pointer items-center gap-3 text-sm text-[var(--home-text-on-dark)]">
                <input
                  type="radio"
                  name="ai-provider-me"
                  className="accent-[var(--home-miami-blue)]"
                  checked={provider === 'openai'}
                  disabled={!summary?.personal.openai_configured}
                  onChange={() => setProvider('openai')}
                />
                <span>OpenAI {!summary?.personal.openai_configured ? '(add a key below first)' : ''}</span>
              </label>
              <label className="flex cursor-pointer items-center gap-3 text-sm text-[var(--home-text-on-dark)]">
                <input
                  type="radio"
                  name="ai-provider-me"
                  className="accent-[var(--home-miami-blue)]"
                  checked={provider === 'anthropic'}
                  disabled={!summary?.personal.anthropic_configured}
                  onChange={() => setProvider('anthropic')}
                />
                <span>Anthropic {!summary?.personal.anthropic_configured ? '(add a key below first)' : ''}</span>
              </label>
              <label className="flex cursor-pointer items-center gap-3 text-sm text-[var(--home-text-on-dark)]">
                <input
                  type="radio"
                  name="ai-provider-me"
                  className="accent-[var(--home-miami-blue)]"
                  checked={provider === 'custom_openai'}
                  onChange={() => setProvider('custom_openai')}
                />
                <span>Custom (OpenAI-compatible)</span>
              </label>
            </fieldset>

            <div className="space-y-4 rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
                Custom endpoint
              </p>
              <label className="block space-y-1.5 text-xs">
                <span className="text-[var(--home-muted-on-dark)]">Base URL (HTTPS)</span>
                <input
                  type="url"
                  autoComplete="off"
                  value={customBaseUrl}
                  onChange={(e) => setCustomBaseUrl(e.target.value)}
                  placeholder="https://your-host/v1"
                  className={`w-full ${SITE_FORM_CONTROL}`}
                />
              </label>
              <label className="block space-y-1.5 text-xs">
                <span className="text-[var(--home-muted-on-dark)]">API key</span>
                <input
                  type="password"
                  autoComplete="off"
                  value={customApiKey}
                  onChange={(e) => setCustomApiKey(e.target.value)}
                  placeholder="Leave blank to keep saved key"
                  className={`w-full ${SITE_FORM_CONTROL}`}
                />
              </label>
              <label className="block space-y-1.5 text-xs">
                <span className="text-[var(--home-muted-on-dark)]">Model id</span>
                <input
                  type="text"
                  value={customModel}
                  onChange={(e) => setCustomModel(e.target.value)}
                  placeholder="Model name at your provider"
                  className={`w-full ${SITE_FORM_CONTROL}`}
                />
              </label>
              <button
                type="button"
                disabled={loading || !summary?.personal.custom_openai_configured || locked}
                onClick={() => void clearCustomProvider()}
                className="rounded-lg border border-white/15 px-3 py-2 text-xs font-medium text-[var(--home-muted-on-dark)] transition hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-40"
              >
                Remove saved custom provider
              </button>
            </div>

            <label className="block space-y-1.5 text-xs">
              <span className="text-[var(--home-muted-on-dark)]">OpenAI API key</span>
              <input
                type="password"
                autoComplete="off"
                value={openaiKey}
                onChange={(e) => setOpenaiKey(e.target.value)}
                placeholder="sk-… (leave blank to keep current saved key)"
                className={`w-full ${SITE_FORM_CONTROL}`}
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={loading || !summary?.personal.openai_configured || locked}
                onClick={() => void clearOpenAiKey()}
                className="rounded-lg border border-white/15 px-3 py-2 text-xs font-medium text-[var(--home-muted-on-dark)] transition hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-40"
              >
                Remove saved OpenAI key
              </button>
            </div>

            <label className="block space-y-1.5 text-xs">
              <span className="text-[var(--home-muted-on-dark)]">Anthropic API key</span>
              <input
                type="password"
                autoComplete="off"
                value={anthropicKey}
                onChange={(e) => setAnthropicKey(e.target.value)}
                placeholder="sk-ant-… (leave blank to keep current saved key)"
                className={`w-full ${SITE_FORM_CONTROL}`}
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={loading || !summary?.personal.anthropic_configured || locked}
                onClick={() => void clearAnthropicKey()}
                className="rounded-lg border border-white/15 px-3 py-2 text-xs font-medium text-[var(--home-muted-on-dark)] transition hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-40"
              >
                Remove saved Anthropic key
              </button>
            </div>

            <label className="block space-y-1.5 text-xs">
              <span className="text-[var(--home-muted-on-dark)]">OpenAI model override</span>
              <input
                type="text"
                value={openaiModel}
                onChange={(e) => setOpenaiModel(e.target.value)}
                placeholder="e.g. gpt-4o (empty = provider default)"
                className={`w-full ${SITE_FORM_CONTROL}`}
              />
            </label>

            <label className="block space-y-1.5 text-xs">
              <span className="text-[var(--home-muted-on-dark)]">Anthropic model override</span>
              <input
                type="text"
                value={anthropicModel}
                onChange={(e) => setAnthropicModel(e.target.value)}
                placeholder="e.g. claude-3-5-sonnet-20241022 (empty = default)"
                className={`w-full ${SITE_FORM_CONTROL}`}
              />
            </label>

            <button
              type="submit"
              disabled={loading || !token || locked}
              className="rounded-xl bg-[var(--home-miami-blue)] px-5 py-2.5 text-sm font-semibold text-[var(--home-zone-void)] transition hover:brightness-105 disabled:opacity-50"
            >
              {loading ? 'Saving…' : 'Save settings'}
            </button>
          </fieldset>
        </form>
      </div>
    </div>
  );
}
